import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormControlLabel, Radio } from "@mui/material";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Checkbox } from "components/ui/Checkbox";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import { ItemFormWrapper } from "features/cdm/components/ItemFormWrapper/ItemFormWrapper";
import {
  ECG_RESULT_OPTIONS,
  ECHO_RESULT_OPTIONS,
  HBA1C_OUT_OF_RANGE_WARNING,
  NT_PROBNP_ALERT,
  OCF_PP_NT_PROBNP_ENABLED,
  YEAR_OPTIONS,
  YES_NO_OPTIONS,
} from "features/cdm/constants/formOptions";
import {
  isLabValueOutOfRange,
  withLabRange,
} from "features/cdm/utils/withLabRange";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./TestAndMedicationForm.module.css";

const ACR_INFO =
  'For investigation of HTN/chronic kidney disease at OCF or PP visit. If result is abnormal, this test can be repeated "if clinically indicated" at next review.';

// PP Ref 43a: Non-HDL Cholesterol is auto-calculated as Total Cholesterol − HDL.
// Sentinel strings (NA) parse to NaN, so the field clears automatically when
// either input is non-numeric.
const computeNonHdl = (total, hdl) => {
  const totalValue = parseFloat(total);
  const hdlValue = parseFloat(hdl);

  if (isNaN(totalValue) || isNaN(hdlValue)) return "";

  const nonHdl = totalValue - hdlValue;

  // Non-HDL is clinically impossible below zero (Total < HDL). Blank the field
  // rather than show a negative value; the Total < HDL inconsistency is flagged
  // as a validation error on the HDL field (see superRefine).
  return nonHdl < 0 ? "" : nonHdl.toFixed(1);
};

export const TestAndMedicationForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
  isAnnualReview = false,
  isRegistration = false,
}) => {
  const formSchema = z
    .object({
      // Mandatory on PP per the Investigations Matrix (CDM 4 v1.2). LDL and eGFR
      // accept the "Not Available" sentinel, which satisfies the required check.
      haemoglobin: withLabRange(
        z.string().min(1, "Haemoglobin is required"),
        "haemoglobin",
        "Haemoglobin"
      ),
      totalCholesterol: withLabRange(
        z.string().min(1, "Total cholesterol is required"),
        "totalCholesterol",
        "Total cholesterol"
      ),
      hdlCholesterol: withLabRange(
        z.string().min(1, "HDL cholesterol is required"),
        "hdlCholesterol",
        "HDL cholesterol"
      ),
      // Auto-calculated (disabled) — derived, so not required here.
      nonHdlCholesterol: z.string().optional(),
      ldlCholesterol: withLabRange(
        z.string().min(1, "LDL cholesterol is required"),
        "ldlCholesterol",
        "LDL cholesterol"
      ),
      triglycerides: withLabRange(
        z.string().min(1, "Triglycerides is required"),
        "triglycerides",
        "Triglycerides"
      ),
      // HbA1c is NOT range-blocked: an out-of-range value (outside 28–150
      // mmol/mol) is submitted as entered and recorded as 0 by the backend per
      // the HL7 spec. An inline warning is shown below the field instead.
      hba1c: z.string().min(1, "HbA1c is required"),
      serumCreatinine: withLabRange(
        z.string().min(1, "Serum creatinine is required"),
        "serumCreatinine",
        "Serum creatinine"
      ),
      egfr: withLabRange(z.string().min(1, "eGFR is required"), "egfr", "eGFR"),
      // Required value-or-NA: enter a result or tick "Not available" (sentinel
      // "NA"). Both accept the NA sentinel per the HL7 spec.
      fastingGlucose: withLabRange(
        z.string().min(1, "Fasting glucose is required"),
        "fastingGlucose",
        "Fasting glucose"
      ),
      acr: withLabRange(z.string().min(1, "ACR is required"), "acr", "ACR"),
      // NT-proBNP — hidden until lab capacity is increased (OCF_PP_NT_PROBNP_ENABLED)
      ntProBNP: withLabRange(z.string().optional(), "ntProBNP", "NT-ProBNP"),
      // User-entered fields — required
      // TFT/LFT are registration-visit-only (spec Ref 51/52 + matrix PP column —
      // not collected at the annual review).
      tftConducted: isAnnualReview
        ? z.string().optional()
        : z.string().min(1, "Please select Yes or No for TFT"),
      lftConducted: isAnnualReview
        ? z.string().optional()
        : z.string().min(1, "Please select Yes or No for LFT"),
      ecgSinceLastReview: z
        .string()
        .min(1, "ECG since last review is required"),
      ecgResult: z.string().optional(),
      lastEcgDate: z.string().optional(),
      echoSinceLastReview: z
        .string()
        .min(1, "Echocardiography since last review is required"),
      echoResult: z.string().optional(),
      lastEchoDate: z.string().optional(),
    })
    .superRefine((values, ctx) => {
      // Non-HDL Cholesterol (Total − HDL) cannot be negative: Total Cholesterol
      // must be at least HDL Cholesterol. Sentinels ("NA") parse to NaN and are
      // skipped.
      const totalValue = parseFloat(values.totalCholesterol);
      const hdlValue = parseFloat(values.hdlCholesterol);

      if (!isNaN(totalValue) && !isNaN(hdlValue) && totalValue < hdlValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Total Cholesterol cannot be less than HDL Cholesterol",
          path: ["hdlCholesterol"],
        });
      }

      if (values.ecgSinceLastReview === "YES" && !values.ecgResult) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ECG result is required when ECG was performed",
          path: ["ecgResult"],
        });
      }

      if (values.ecgSinceLastReview === "YES" && !values.lastEcgDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last ECG date is required when ECG was performed",
          path: ["lastEcgDate"],
        });
      }

      if (values.echoSinceLastReview === "YES" && !values.echoResult) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Echocardiography result is required when echo was performed",
          path: ["echoResult"],
        });
      }

      // BE requires last echo date in the XML message when an echo was done —
      // flag it on this step instead of failing at submit.
      if (values.echoSinceLastReview === "YES" && !values.lastEchoDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last echo date is required when echo was performed",
          path: ["lastEchoDate"],
        });
      }
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      haemoglobin: data?.haemoglobin || "",
      totalCholesterol: data?.totalCholesterol || "",
      hdlCholesterol: data?.hdlCholesterol || data?.tcHdlRatio || "",
      nonHdlCholesterol: data?.nonHdlCholesterol || "",
      ldlCholesterol: data?.ldlCholesterol || "",
      triglycerides: data?.triglycerides || "",
      hba1c: data?.hba1c || "",
      serumCreatinine: data?.serumCreatinine || "",
      egfr: data?.egfr || "",
      fastingGlucose: data?.fastingGlucose || "",
      acr: data?.acr || "",
      ntProBNP: data?.ntProBNP || "",
      tftConducted: data?.tftConducted || "",
      lftConducted: data?.lftConducted || "",
      ecgSinceLastReview: data?.ecgSinceLastReview || "",
      ecgResult: data?.ecgResult || "",
      lastEcgDate: data?.lastEcgDate || "",
      echoSinceLastReview: data?.echoSinceLastReview || "",
      echoResult: data?.echoResult || "",
      lastEchoDate: data?.lastEchoDate || "",
    },
  });

  const watchedTotalCholesterol = form.watch("totalCholesterol");
  const watchedHdlCholesterol = form.watch("hdlCholesterol");
  const isHba1cOutOfRange = isLabValueOutOfRange("hba1c", form.watch("hba1c"));

  useEffect(() => {
    form.setValue(
      "nonHdlCholesterol",
      computeNonHdl(watchedTotalCholesterol, watchedHdlCholesterol),
      { shouldValidate: true }
    );
  }, [watchedTotalCholesterol, watchedHdlCholesterol, form]);

  const ecgPerformed = form.watch("ecgSinceLastReview");
  const echoPerformed = form.watch("echoSinceLastReview");

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestAndMedicationForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestAndMedicationForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Investigations</h3>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="haemoglobin">
                  <label className={style.addReferralFormLabel}>
                    Haemoglobin (FBC)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("haemoglobin")}
                  />
                </FormField>
                <FormField name="totalCholesterol">
                  <label className={style.addReferralFormLabel}>
                    Total Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("totalCholesterol")}
                  />
                </FormField>
                <FormField name="hdlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    HDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("hdlCholesterol")}
                  />
                </FormField>
                <FormField name="nonHdlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    Non-HDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={form.watch("nonHdlCholesterol") ?? ""}
                  />
                </FormField>
                <FormField name="ldlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    LDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled={isReview || form.watch("ldlCholesterol") === "NA"}
                    {...form.register("ldlCholesterol")}
                    value={
                      form.watch("ldlCholesterol") === "NA"
                        ? "Not available"
                        : form.watch("ldlCholesterol") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("ldlCholesterol", e.target.value, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("ldlCholesterol") === "NA"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue(
                          "ldlCholesterol",
                          e.target.checked ? "NA" : "",
                          { shouldValidate: true }
                        )
                      }
                    />
                    Not available
                  </label>
                </FormField>
              </div>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="triglycerides">
                  <label className={style.addReferralFormLabel}>
                    Triglycerides (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("triglycerides")}
                  />
                </FormField>
                <FormField name="hba1c">
                  <label className={style.addReferralFormLabel}>HbA1c</label>
                  <div className={style.mmolInput}>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="numeric"
                      onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          ""
                        ))
                      }
                      disabled={isReview}
                      {...form.register("hba1c")}
                    />
                  </div>
                  {isHba1cOutOfRange && (
                    <p className={style.hba1cWarning}>
                      {HBA1C_OUT_OF_RANGE_WARNING}
                    </p>
                  )}
                </FormField>
                <FormField name="serumCreatinine">
                  <label className={style.addReferralFormLabel}>
                    Serum Creatinine
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("serumCreatinine")}
                  />
                </FormField>
              </div>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="egfr">
                  <label className={style.addReferralFormLabel}>
                    eGFR (Urea & Electrolytes)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled={isReview || form.watch("egfr") === "NA"}
                    {...form.register("egfr")}
                    value={
                      form.watch("egfr") === "NA"
                        ? "Not available"
                        : form.watch("egfr") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("egfr", e.target.value, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("egfr") === "NA"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue("egfr", e.target.checked ? "NA" : "", {
                          shouldValidate: true,
                        })
                      }
                    />
                    Not available
                  </label>
                </FormField>
                <FormField name="fastingGlucose">
                  <label className={style.addReferralFormLabel}>
                    Fasting Glucose
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled={isReview || form.watch("fastingGlucose") === "NA"}
                    value={
                      form.watch("fastingGlucose") === "NA"
                        ? "Not available"
                        : form.watch("fastingGlucose") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("fastingGlucose", e.target.value, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("fastingGlucose") === "NA"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue(
                          "fastingGlucose",
                          e.target.checked ? "NA" : "",
                          { shouldValidate: true }
                        )
                      }
                    />
                    Not available
                  </label>
                </FormField>
                <FormField name="acr">
                  <label className={style.addReferralFormLabel}>
                    Albumin/Creatinine Ratio (ACR)
                    <InfoTooltip text={ACR_INFO} />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    disabled={isReview || form.watch("acr") === "NA"}
                    value={
                      form.watch("acr") === "NA"
                        ? "Not available"
                        : form.watch("acr") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue(
                        "acr",
                        e.target.value.replace(/[^0-9.]/g, ""),
                        {
                          shouldValidate: true,
                        }
                      )
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("acr") === "NA"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue("acr", e.target.checked ? "NA" : "", {
                          shouldValidate: true,
                        })
                      }
                    />
                    Not available
                  </label>
                  <p className={style.acrInfo}>
                    For investigation of HTN / chronic kidney disease at the OCF
                    or PP visit. If the result is abnormal, this test can be
                    repeated &quot;if clinically indicated&quot; at the next
                    review.
                  </p>
                </FormField>
                {!!OCF_PP_NT_PROBNP_ENABLED && (
                  <FormField name="ntProBNP">
                    <label className={style.addReferralFormLabel}>
                      NT-proBNP
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="numeric"
                      disabled={isReview || form.watch("ntProBNP") === "NA"}
                      value={
                        form.watch("ntProBNP") === "NA"
                          ? "Not available"
                          : form.watch("ntProBNP") ?? ""
                      }
                      onChange={(e) =>
                        form.setValue(
                          "ntProBNP",
                          e.target.value.replace(/[^0-9.]/g, ""),
                          { shouldValidate: true }
                        )
                      }
                    />
                    <label className={style.naCheckbox}>
                      <Checkbox
                        checked={form.watch("ntProBNP") === "NA"}
                        disabled={isReview}
                        onChange={(e) =>
                          form.setValue(
                            "ntProBNP",
                            e.target.checked ? "NA" : "",
                            { shouldValidate: true }
                          )
                        }
                      />
                      Not available
                    </label>
                  </FormField>
                )}
                {!isAnnualReview && (
                  <>
                    <FormField name="tftConducted">
                      <label className={style.addReferralFormLabel}>
                        TFT Conducted?
                        <span className={style.requiredMark}>*</span>
                      </label>
                      <div className={style.radioGroup}>
                        {YES_NO_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                disabled={isReview}
                                size="small"
                                checked={
                                  form.watch("tftConducted") === option.value
                                }
                                onChange={(e) =>
                                  form.setValue("tftConducted", e.target.value, {
                                    shouldValidate: true,
                                  })
                                }
                                sx={{
                                  "&.Mui-checked": { color: "var(--green-900)" },
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </div>
                    </FormField>
                    <FormField name="lftConducted">
                      <label className={style.addReferralFormLabel}>
                        LFT Conducted?
                        <span className={style.requiredMark}>*</span>
                      </label>
                      <div className={style.radioGroup}>
                        {YES_NO_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                disabled={isReview}
                                size="small"
                                checked={
                                  form.watch("lftConducted") === option.value
                                }
                                onChange={(e) =>
                                  form.setValue("lftConducted", e.target.value, {
                                    shouldValidate: true,
                                  })
                                }
                                sx={{
                                  "&.Mui-checked": { color: "var(--green-900)" },
                                }}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </div>
                    </FormField>
                  </>
                )}
              </div>
              {/* NEW 19-03-2024 (CDM dataset V3.1, row 63b): the NTproBNP
                  indications alert must show on EVERY review across Treatment,
                  PP & OCF — rendered independently of the NT-proBNP field
                  (gated off in OCF/PP by OCF_PP_NT_PROBNP_ENABLED). Placed
                  under the test-results grid as a full-width note, not tied to
                  a single column. */}
              <p className={style.ntProBnpAlert}>{NT_PROBNP_ALERT}</p>
            </div>
            <div
              className={style.itemWrapper}
              style={{
                marginBottom: "0px",
                paddingBottom: "0px",
                borderBottom: "none",
              }}
            >
              <h3>Diagnostic Investigations</h3>
              <div
                className={style.diagnosticColumns}
                style={{ marginTop: "16px" }}
              >
                <div className={style.diagnosticGroup}>
                  <FormField name="ecgSinceLastReview">
                    <label className={style.addReferralFormLabel}>
                      {isRegistration
                        ? "Has the patient had an ECG?"
                        : "Has the patient had an ECG since the last review?"}
                    </label>
                    <Select
                      placeholder="Select"
                      options={YES_NO_OPTIONS}
                      value={form.watch("ecgSinceLastReview")}
                      onChange={(value) => {
                        form.setValue("ecgSinceLastReview", value, {
                          shouldValidate: true,
                        });
                        // The result and date only exist for a performed ECG —
                        // clear both, or a value picked before switching to No
                        // stays in form state (hidden) and is still submitted.
                        if (value !== "YES") {
                          form.setValue("ecgResult", "");
                          form.setValue("lastEcgDate", "");
                        }
                      }}
                      disabled={isReview}
                      required
                    />
                  </FormField>
                  {ecgPerformed === "YES" && (
                    <FormField name="ecgResult">
                      <label className={style.addReferralFormLabel}>
                        ECG Result
                      </label>
                      <Select
                        placeholder="Select"
                        options={ECG_RESULT_OPTIONS}
                        value={form.watch("ecgResult")}
                        onChange={(value) =>
                          form.setValue("ecgResult", value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={isReview}
                        required
                      />
                    </FormField>
                  )}
                  {ecgPerformed === "YES" && (
                    <FormField name="lastEcgDate">
                      <label className={style.addReferralFormLabel}>
                        {isRegistration
                          ? "ECG Date (Year)"
                          : "Last ECG Date (Year)"}
                      </label>
                      <Select
                        placeholder="Select year"
                        options={YEAR_OPTIONS}
                        value={form.watch("lastEcgDate")}
                        onChange={(value) =>
                          form.setValue("lastEcgDate", value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={isReview}
                      />
                    </FormField>
                  )}
                </div>
                <div className={style.diagnosticGroup}>
                  <FormField name="echoSinceLastReview">
                    <label className={style.addReferralFormLabel}>
                      {isRegistration
                        ? "Has the patient had an Echocardiography?"
                        : "Has the patient had an Echocardiography since the last review?"}
                    </label>
                    <Select
                      placeholder="Select"
                      options={YES_NO_OPTIONS}
                      value={form.watch("echoSinceLastReview")}
                      onChange={(value) => {
                        form.setValue("echoSinceLastReview", value, {
                          shouldValidate: true,
                        });
                        // Clear the date too — see the ECG note above.
                        if (value !== "YES") {
                          form.setValue("echoResult", "");
                          form.setValue("lastEchoDate", "");
                        }
                      }}
                      disabled={isReview}
                      required
                    />
                  </FormField>
                  {echoPerformed === "YES" && (
                    <FormField name="echoResult">
                      <label className={style.addReferralFormLabel}>
                        Echocardiography Result
                      </label>
                      <Select
                        placeholder="Select"
                        options={ECHO_RESULT_OPTIONS}
                        value={form.watch("echoResult")}
                        onChange={(value) =>
                          form.setValue("echoResult", value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={isReview}
                        required
                      />
                    </FormField>
                  )}
                  {echoPerformed === "YES" && (
                    <FormField name="lastEchoDate">
                      <label className={style.addReferralFormLabel}>
                        {isRegistration
                          ? "Echocardiography Date (Year)"
                          : "Last Echo Date (Year)"}
                      </label>
                      <Select
                        placeholder="Select year"
                        options={YEAR_OPTIONS}
                        value={form.watch("lastEchoDate")}
                        onChange={(value) =>
                          form.setValue("lastEchoDate", value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={isReview}
                      />
                    </FormField>
                  )}
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
